import {faLink} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Card, CardContent, CardHeader, Divider, InputLabel, Stack} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {ActionParams, BaseOption} from '@refinedev/core';
import dayjs from 'dayjs';
import {FC, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {subscriptionStatusOptions} from '@components/chip';
import {TissotField} from '@components/form/hook-form';
import {Field} from '@minimal/components/hook-form';
import {SubscriptionEnum} from '@pages/pilotage-rh/accounts/account-types';

const getFieldNames = (index: number) => ({
  service: `subscriptions.${index}.service` as const,
  code: `subscriptions.${index}.code` as const,
  subscribedAt: `subscriptions.${index}.subscribedAt` as const,
  monthOfSubscription: `subscriptions.${index}.monthOfSubscription` as const,
  fromApi: `subscriptions.${index}.fromApi` as const,
});

const subscriptionsServices: BaseOption[] = [
  {value: 'PRH', label: 'Pilotage RH'},
  {value: 'EGAPRO', label: 'Index EgaPro'},
  {value: 'TRS', label: 'Transparence Salariale'},
];

const allowedMonthValues = [0.5, ...Array.from({length: 14}, (_, monthIndex) => monthIndex + 1)];

const normalizeMonthOfSubscription = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return value;
  }

  if (allowedMonthValues.includes(value)) {
    return value;
  }

  if (value < 1) {
    return 0.5;
  }

  const rounded = Math.min(14, Math.max(1, Math.round(value)));
  return rounded;
};

type SubscriptionData = {
  service?: string;
  code?: string;
  subscribedAt?: string;
  monthOfSubscription?: number;
  fromApi?: boolean;
  expiresAt?: string;
};

interface SubscriptionsFormProps extends ActionParams {
  error?: boolean;
}

export const SubscriptionsForm: FC<SubscriptionsFormProps> = ({error}) => {
  const {t} = useTranslation();

  const {control, setValue} = useFormContext();
  const {fields, append} = useFieldArray({
    control,
    name: 'subscriptions',
  });

  const subscriptionsData = useWatch({
    control,
    name: 'subscriptions',
  });

  const normalizedSubscriptionsData = useMemo(() => {
    return subscriptionsData?.map((sub: SubscriptionData) => ({
      ...sub,
      monthOfSubscription: normalizeMonthOfSubscription(sub.monthOfSubscription),
    }));
  }, [subscriptionsData]);

  useEffect(() => {
    normalizedSubscriptionsData?.forEach((sub: SubscriptionData, index: number) => {
      if (
        !sub ||
        normalizedSubscriptionsData[index]?.monthOfSubscription === subscriptionsData?.[index]?.monthOfSubscription
      ) {
        return;
      }

      setValue(`subscriptions.${index}.monthOfSubscription`, normalizedSubscriptionsData[index]?.monthOfSubscription, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, [normalizedSubscriptionsData, subscriptionsData, setValue]);

  useEffect(() => {
    subscriptionsData?.forEach((sub: SubscriptionData, index: number) => {
      if (!sub || !sub.service) {
        return;
      }

      const isServiceStillValid = subscriptionsServices.some(
        (option) =>
          option.value === sub.service ||
          !subscriptionsData?.some((s: SubscriptionData, i: number) => s.service === option.value && i !== index),
      );

      if (!isServiceStillValid) {
        const validService = subscriptionsServices.find(
          (option) =>
            option.value !== sub.service &&
            !subscriptionsData?.some((s: SubscriptionData, i: number) => s.service === option.value && i !== index),
        );

        if (validService) {
          setValue(`subscriptions.${index}.service`, validService.value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    });
  }, [setValue, subscriptionsData]);

  const defaultValues = {
    service: subscriptionsServices.find(
      (option) => !subscriptionsData.some((sub: SubscriptionData) => sub.service === option.value),
    )?.value,
    monthOfSubscription: subscriptionsData.some((sub: SubscriptionData) => sub.service === 'PRH') ? 0.5 : 1,
    code: SubscriptionEnum.SUBSCRIPTION_NONE,
    subscribedAt: new Date().toISOString(),
  };

  return (
    <Card style={{maxWidth: '600px'}}>
      <CardHeader title={t('pages.pilotage-rh.account.fields.subscriptions')} />
      <CardContent>
        <Stack divider={<Divider />} spacing={6}>
          {error && (
            <Typography variant="body2" color="error.main">
              {t('pages.pilotage-rh.account.notifications.subscription.error')}
            </Typography>
          )}
          {fields.map((field, index) => {
            const sub = normalizedSubscriptionsData?.[index];
            const isDisabled = sub?.fromApi || false;
            return (
              <Stack key={field.id} gap={2}>
                <TissotField.Select
                  id={getFieldNames(index).service}
                  name={getFieldNames(index).service}
                  label={t('pages.pilotage-rh.account.fields.service')}
                  options={subscriptionsServices.filter(
                    (option) =>
                      !subscriptionsData?.some(
                        (s: SubscriptionData, i: number) => s.service === option.value && i !== index,
                      ),
                  )}
                  disabled={isDisabled}
                />
                <InputLabel>{t('pages.pilotage-rh.account.fields.subscription_type')}</InputLabel>
                {sub?.fromApi && (
                  <Stack>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, pb: 3}}>
                      <FontAwesomeIcon icon={faLink} size={'xs'} color={'info'} />
                      <Typography color={'info'} variant={'subtitle2'}>
                        {t('pages.pilotage-rh.account.api')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color={'textPrimary'} variant={'caption'}>
                        {t('pages.pilotage-rh.account.fields.expiresAt')} : {dayjs(sub?.expiresAt).format('DD/MM/YYYY')}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                <Stack spacing={2} display={isDisabled ? 'none' : 'inherit'}>
                  <Field.RadioGroup
                    name={getFieldNames(index).code}
                    options={subscriptionStatusOptions}
                    disabled={isDisabled}
                  />
                  <Field.DatePicker
                    name={getFieldNames(index).subscribedAt}
                    label={t('pages.pilotage-rh.account.fields.subscribedAt')}
                    format="DD/MM/YYYY"
                    disabled={isDisabled}
                  />
                  <TissotField.Select
                    id={getFieldNames(index).monthOfSubscription}
                    name={getFieldNames(index).monthOfSubscription}
                    label={t('pages.pilotage-rh.account.fields.monthOfSubscription')}
                    options={[
                      {
                        value: 0.5,
                        label: t('pages.pilotage-rh.account.fields.numberOfDays', {number: 15}),
                      },
                      ...Array.from({length: 14}, (_, monthIndex) => ({
                        value: monthIndex + 1,
                        label: t('pages.pilotage-rh.account.fields.numberOfMonths', {number: monthIndex + 1}),
                      })),
                    ]}
                    disabled={isDisabled}
                  />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
      <Button
        variant="soft"
        color="primary"
        disabled={fields.length >= subscriptionsServices.length}
        onClick={() => append(defaultValues)}
      >
        {t('pages.pilotage-rh.account.fields.add_subscription')}
      </Button>
    </Card>
  );
};
